/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { HttpParams } from "@angular/common/http";
import { of, throwError } from "rxjs";
import { ApplicationError } from "../../../../models/ui/application-error.model";
import { HttpRequestOptions } from "../../../../models/ui/http-request-options.model";
import {
	GET_ALL_COUNTRIES_RESPONSE,
	GET_ALL_MANUFACTURERS_RESPONSE,
	GET_CALCULATION_KPIS_RESPONSE,
	GET_CARBON_FOOTPRINT_BY_SCOPE_PERCENTAGE_RESPONSE,
	GET_CARBON_FOOTPRINT_BY_SCOPE_VALUE_RESPONSE,
	GET_CARBON_FOOTPRINT_RESPONSE,
	GET_CATEGORIES_RESPONSE,
	GET_COMPUTE_RESPONSE,
	GET_EMISSION_BY_MANUFACTURER_RESPONSE,
	GET_EMISSION_BY_SCOPE_RESPONSE,
	GET_EMISSION_OVERVIEW_FOR_BAR_CHART_RESPONSE,
	GET_EMISSION_OVERVIEW_RESPONSE,
	GET_FILES_RESPONSE,
	GET_GENERAL_CATEGORIES_RESPONSE,
	GET_GENERAL_INFORMATION_RESPONSE,
	GET_MANUFACTURERS_EMISSION_RESPONSE,
	GET_MANUFACTURERS_RESPONSE,
	GET_SAVE_CALCULATION_RESPONSE,
	GET_SPECIFIC_CATEGORIES_RESPONSE,
	GET_TOTAL_EMISSION_BY_CATEGORY_RESPONSE,
	GET_TOTAL_EMISSION_BY_MANUFACTURER_PERCENTAGE_RESPONSE,
	GET_TOTAL_EMISSION_BY_MANUFACTURER_VALUE_RESPONSE,
	GET_TOTAL_EMISSION_FOR_PIE_CHART_RESPONSE,
	GET_TOTAL_EMISSION_RESPONSE,
	GET_CO2_BY_INSTALL_STATUS_DISTRIBUTION,
	GET_EMISSIONS_BY_MODEL_RESPONSE,
	GET_EMISSIONS_BY_MODEL_PER_DEVICE_RESPONSE,
	GET_EMISSIONS_BY_REGION_RESPONSE,
	GET_EMISSIONS_BY_REGION_PER_DEVICE_RESPONSE,
	GET_EMISSIONS_OVER_TIME_RESPONSE,
	GET_SAVED_LIFECYCLE_EMISSIONS_RESPONSE,
	GET_EMISSIONS_BY_COUNTRY_OVER_TIME_RESPONSE
} from "./dashboard-hardware-assets.db";

export class DashboardHardwareAssets {
	public static renameFile(options: HttpRequestOptions) {
		const { params, pathParams, body } = options;

		const httpParams = params as HttpParams;

		if (!httpParams)
			return throwError(
				() =>
					new ApplicationError(
						"No params provided",
						this.constructor.name,
						this.renameFile.name
					)
			);

		const tenantId = httpParams.get("tenantId");

		if (!tenantId)
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						this.constructor.name,
						this.renameFile.name
					)
			);

		const fileId = pathParams?.["fileId"];

		if (!fileId)
			return throwError(
				() =>
					new ApplicationError(
						"No file id provided",
						this.constructor.name,
						this.renameFile.name
					)
			);

		if (!body)
			return throwError(
				() =>
					new ApplicationError(
						"No file name provided",
						this.constructor.name,
						this.renameFile.name
					)
			);

		return of("File renamed successfully");
	}

	public static deleteFile(options: HttpRequestOptions) {
		const { params, pathParams } = options;

		const httpParams = params as HttpParams;

		if (!httpParams)
			return throwError(
				() =>
					new ApplicationError(
						"No params provided",
						this.constructor.name,
						this.deleteFile.name
					)
			);

		const tenantId = httpParams.get("tenantId");

		if (!tenantId)
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						this.constructor.name,
						this.deleteFile.name
					)
			);

		const fileId = pathParams?.["fileId"];

		if (!fileId)
			return throwError(
				() =>
					new ApplicationError(
						"No file id provided",
						this.constructor.name,
						this.deleteFile.name
					)
			);

		return of("File deleted successfully");
	}

	public static uploadFile(options: HttpRequestOptions) {
		const { params, body } = options;

		const httpParams = params as HttpParams;

		if (!httpParams)
			return throwError(
				() =>
					new ApplicationError(
						"No params provided",
						this.constructor.name,
						this.uploadFile.name
					)
			);

		const tenantId = httpParams.get("tenantId");

		if (!tenantId)
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						this.constructor.name,
						this.uploadFile.name
					)
			);

		if (!body)
			return throwError(
				() =>
					new ApplicationError(
						"No file provided",
						this.constructor.name,
						this.uploadFile.name
					)
			);

		return of({ fileId: "1234567890" });
	}

	public static downloadAsCsv(options: HttpRequestOptions) {
		const { params, pathParams } = options;

		const httpParams = params as HttpParams;

		if (!httpParams)
			return throwError(
				() =>
					new ApplicationError(
						"No params provided",
						this.constructor.name,
						this.downloadAsCsv.name
					)
			);

		const tenantId = httpParams.get("tenantId");

		if (!tenantId)
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						this.constructor.name,
						this.downloadAsCsv.name
					)
			);

		const fileId = pathParams?.["fileId"];

		if (!fileId)
			return throwError(
				() =>
					new ApplicationError(
						"No file id provided",
						this.constructor.name,
						this.downloadAsCsv.name
					)
			);

		return of("");
	}

	public static getUploadingStatus(options: HttpRequestOptions) {
		const { params, pathParams } = options;

		const httpParams = params as HttpParams;

		if (!httpParams)
			return throwError(
				() =>
					new ApplicationError(
						"No params provided",
						this.constructor.name,
						this.getUploadingStatus.name
					)
			);

		const tenantId = httpParams.get("tenantId");

		if (!tenantId)
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						this.constructor.name,
						this.getUploadingStatus.name
					)
			);

		const fileId = pathParams?.["fileId"];

		if (!fileId)
			return throwError(
				() =>
					new ApplicationError(
						"No file id provided",
						this.constructor.name,
						this.getUploadingStatus.name
					)
			);

		return of({ status: "DONE" });
	}

	public static downloadTemplate() {
		return of(new Blob());
	}

	public static getFiles(options: HttpRequestOptions) {
		const { pathParams } = options;

		const tenantId = pathParams?.["tenantId"];

		if (!tenantId)
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						this.constructor.name,
						this.getFiles.name
					)
			);

		return of(GET_FILES_RESPONSE);
	}

	public static getCategories(options: HttpRequestOptions) {
		const { params } = options;

		const httpParams = params as HttpParams;

		if (!httpParams)
			return throwError(
				() =>
					new ApplicationError(
						"No params provided",
						this.constructor.name,
						this.getCategories.name
					)
			);

		const tenantId = httpParams.get("tenantId");

		if (!tenantId)
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						this.constructor.name,
						this.getCategories.name
					)
			);

		return of(GET_CATEGORIES_RESPONSE);
	}

	public static getGeneralInformation(options: HttpRequestOptions) {
		const { params, pathParams } = options;

		const httpParams = params as HttpParams;

		if (!httpParams)
			return throwError(
				() =>
					new ApplicationError(
						"No params provided",
						this.constructor.name,
						this.getGeneralInformation.name
					)
			);

		const tenantId = httpParams.get("tenantId");

		if (!tenantId)
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						this.constructor.name,
						this.getGeneralInformation.name
					)
			);

		const fileId = pathParams?.["fileId"];

		if (!fileId)
			return throwError(
				() =>
					new ApplicationError(
						"No file id provided",
						this.constructor.name,
						this.getGeneralInformation.name
					)
			);

		return of(GET_GENERAL_INFORMATION_RESPONSE);
	}

	public static getTotalEmissionForPieChart(options: HttpRequestOptions) {
		const { params, pathParams, body } = options;

		const httpParams = params as HttpParams;

		if (!httpParams)
			return throwError(
				() =>
					new ApplicationError(
						"No params provided",
						this.constructor.name,
						this.getTotalEmissionForPieChart.name
					)
			);

		const tenantId = httpParams.get("tenantId");

		if (!tenantId)
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						this.constructor.name,
						this.getTotalEmissionForPieChart.name
					)
			);

		const fileId = pathParams?.["fileId"];

		if (!fileId)
			return throwError(
				() =>
					new ApplicationError(
						"No file id provided",
						this.constructor.name,
						this.getTotalEmissionForPieChart.name
					)
			);

		if (!body)
			return throwError(
				() =>
					new ApplicationError(
						"No categories provided",
						this.constructor.name,
						this.getTotalEmissionForPieChart.name
					)
			);

		return of(GET_TOTAL_EMISSION_FOR_PIE_CHART_RESPONSE);
	}

	public static getEmissionsByModel(options: HttpRequestOptions) {
		// const { params, pathParams, body } = options;

		// const httpParams = params as HttpParams;

		return of(GET_EMISSIONS_BY_MODEL_RESPONSE);
	}

	public static getEmissionsByModelPerDevice(options: HttpRequestOptions) {
		return of(GET_EMISSIONS_BY_MODEL_PER_DEVICE_RESPONSE);
	}

	public static getEmissionsByRegion(options: HttpRequestOptions) {
		const { params, pathParams, body } = options;

		const httpParams = params as HttpParams;

		return of(GET_EMISSIONS_BY_REGION_RESPONSE);
	}

	public static getEmissionsByRegionPerDevice(options: HttpRequestOptions) {
		return of(GET_EMISSIONS_BY_REGION_PER_DEVICE_RESPONSE);
	}

	public static getCo2ByInstallStatus(options: HttpRequestOptions) {
		const { params, pathParams, body } = options;

		const httpParams = params as HttpParams;

		// return throwError(
		// 	() =>
		// 		new ApplicationError(
		// 			"No params provided",
		// 			this.constructor.name,
		// 			this.getTotalEmissionForPieChart.name
		// 		)
		// );

		if (!httpParams)
			return throwError(
				() =>
					new ApplicationError(
						"No params provided",
						this.constructor.name,
						this.getTotalEmissionForPieChart.name
					)
			);

		const fileId = pathParams?.["fileId"];

		if (!fileId)
			return throwError(
				() =>
					new ApplicationError(
						"No file id provided",
						this.constructor.name,
						this.getTotalEmissionForPieChart.name
					)
			);

		return of(GET_CO2_BY_INSTALL_STATUS_DISTRIBUTION);
	}

	public static getEmissionsOverTime(options: HttpRequestOptions) {
		const { params, pathParams } = options;
		const httpParams = params as HttpParams;

		return of(GET_EMISSIONS_OVER_TIME_RESPONSE);
	}

	public static getSavedLifecycleEmissions(options: HttpRequestOptions) {
		const { params, pathParams } = options;
		const httpParams = params as HttpParams;

		return of(GET_SAVED_LIFECYCLE_EMISSIONS_RESPONSE);
	}

	public static getEmissionsByCountryOverTime(options: HttpRequestOptions) {
		const { params, pathParams, body } = options;

		const httpParams = params as HttpParams;

		return of(GET_EMISSIONS_BY_COUNTRY_OVER_TIME_RESPONSE);
	}

	public static getManufacturersEmission(options: HttpRequestOptions) {
		const { params, pathParams, body } = options;

		const httpParams = params as HttpParams;

		if (!httpParams)
			return throwError(
				() =>
					new ApplicationError(
						"No params provided",
						this.constructor.name,
						this.getManufacturersEmission.name
					)
			);

		const tenantId = httpParams.get("tenantId");

		if (!tenantId)
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						this.constructor.name,
						this.getManufacturersEmission.name
					)
			);

		const fileId = pathParams?.["fileId"];

		if (!fileId)
			return throwError(
				() =>
					new ApplicationError(
						"No file id provided",
						this.constructor.name,
						this.getManufacturersEmission.name
					)
			);

		if (!body)
			return throwError(
				() =>
					new ApplicationError(
						"No categories provided",
						this.constructor.name,
						this.getManufacturersEmission.name
					)
			);

		return of(GET_MANUFACTURERS_EMISSION_RESPONSE);
	}

	public static getCarbonFootprint(options: HttpRequestOptions) {
		const { params, pathParams } = options;

		const httpParams = params as HttpParams;

		if (!httpParams)
			return throwError(
				() =>
					new ApplicationError(
						"No params provided",
						this.constructor.name,
						this.getCarbonFootprint.name
					)
			);

		const tenantId = httpParams.get("tenantId");

		if (!tenantId)
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						this.constructor.name,
						this.getCarbonFootprint.name
					)
			);

		const fileId = pathParams?.["fileId"];

		if (!fileId)
			return throwError(
				() =>
					new ApplicationError(
						"No file id provided",
						this.constructor.name,
						this.getCarbonFootprint.name
					)
			);

		return of(GET_CARBON_FOOTPRINT_RESPONSE);
	}

	public static getCarbonFootprintByScope(options: HttpRequestOptions) {
		const { params, pathParams, body } = options;

		const httpParams = params as HttpParams;

		if (!httpParams)
			return throwError(
				() =>
					new ApplicationError(
						"No params provided",
						this.constructor.name,
						this.getCarbonFootprintByScope.name
					)
			);

		const tenantId = httpParams.get("tenantId");
		const mode = httpParams.get("mode");

		if (!tenantId || !mode)
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						this.constructor.name,
						this.getCarbonFootprintByScope.name
					)
			);

		const fileId = pathParams?.["fileId"];

		if (!fileId)
			return throwError(
				() =>
					new ApplicationError(
						"No file id provided",
						this.constructor.name,
						this.getCarbonFootprintByScope.name
					)
			);

		if (!body)
			return throwError(
				() =>
					new ApplicationError(
						"No manufacturers provided",
						this.constructor.name,
						this.getCarbonFootprintByScope.name
					)
			);

		return of(
			mode === "PERCENTAGE"
				? GET_CARBON_FOOTPRINT_BY_SCOPE_PERCENTAGE_RESPONSE
				: GET_CARBON_FOOTPRINT_BY_SCOPE_VALUE_RESPONSE
		);
	}

	public static getManufacturers(options: HttpRequestOptions) {
		const { params, pathParams } = options;

		const httpParams = params as HttpParams;

		if (!httpParams)
			return throwError(
				() =>
					new ApplicationError(
						"No params provided",
						this.constructor.name,
						this.getManufacturers.name
					)
			);

		const tenantId = httpParams.get("tenantId");

		if (!tenantId)
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						this.constructor.name,
						this.getManufacturers.name
					)
			);

		const fileId = pathParams?.["fileId"];

		if (!fileId)
			return throwError(
				() =>
					new ApplicationError(
						"No file id provided",
						this.constructor.name,
						this.getManufacturers.name
					)
			);

		return of(GET_MANUFACTURERS_RESPONSE);
	}

	public static getAllCountries() {
		return of(GET_ALL_COUNTRIES_RESPONSE);
	}

	public static getAllManufacturers() {
		return of(GET_ALL_MANUFACTURERS_RESPONSE);
	}

	public static compute(options: HttpRequestOptions) {
		const { body } = options;

		if (!body)
			return throwError(
				() =>
					new ApplicationError(
						"No body provided",
						this.constructor.name,
						this.compute.name
					)
			);

		return of(GET_COMPUTE_RESPONSE);
	}

	public static getGeneralCategories() {
		return of(GET_GENERAL_CATEGORIES_RESPONSE);
	}

	public static getSpecificCategories(options: HttpRequestOptions) {
		const { pathParams } = options;

		const category = pathParams?.["category"];

		if (!category)
			return throwError(
				() =>
					new ApplicationError(
						"No category provided",
						this.constructor.name,
						this.getSpecificCategories.name
					)
			);

		return of(GET_SPECIFIC_CATEGORIES_RESPONSE);
	}

	public static getTotalEmission(options: HttpRequestOptions) {
		const { params, pathParams } = options;

		const httpParams = params as HttpParams;

		if (!httpParams)
			return throwError(
				() =>
					new ApplicationError(
						"No params provided",
						this.constructor.name,
						this.getTotalEmission.name
					)
			);

		const tenantId = httpParams.get("tenantId");

		if (!tenantId)
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						this.constructor.name,
						this.getTotalEmission.name
					)
			);

		const calculationId = pathParams?.["calculationId"];

		if (!calculationId)
			return throwError(
				() =>
					new ApplicationError(
						"No calculation id provided",
						this.constructor.name,
						this.getTotalEmission.name
					)
			);

		return of(GET_TOTAL_EMISSION_RESPONSE);
	}

	public static getEmissionOverview(options: HttpRequestOptions) {
		const { pathParams } = options;

		const calculationId = pathParams?.["calculationId"];

		if (!calculationId)
			return throwError(
				() =>
					new ApplicationError(
						"No calculation id provided",
						this.constructor.name,
						this.getEmissionOverview.name
					)
			);

		return of(GET_EMISSION_OVERVIEW_RESPONSE);
	}

	public static getCalculationKpis(options: HttpRequestOptions) {
		const { pathParams } = options;

		const calculationId = pathParams?.["calculationId"];

		if (!calculationId)
			return throwError(
				() =>
					new ApplicationError(
						"No calculation id provided",
						this.constructor.name,
						this.getCalculationKpis.name
					)
			);

		return of(GET_CALCULATION_KPIS_RESPONSE);
	}

	public static getEmissionByScope(options: HttpRequestOptions) {
		const { pathParams } = options;

		const calculationId = pathParams?.["calculationId"];

		if (!calculationId)
			return throwError(
				() =>
					new ApplicationError(
						"No calculation id provided",
						this.constructor.name,
						this.getCalculationKpis.name
					)
			);

		return of(GET_EMISSION_BY_SCOPE_RESPONSE);
	}

	public static getEmissionByManufacturer(options: HttpRequestOptions) {
		const { pathParams } = options;

		const calculationId = pathParams?.["calculationId"];

		if (!calculationId)
			return throwError(
				() =>
					new ApplicationError(
						"No calculation id provided",
						this.constructor.name,
						this.getCalculationKpis.name
					)
			);

		return of(GET_EMISSION_BY_MANUFACTURER_RESPONSE);
	}

	public static saveCalculation(options: HttpRequestOptions) {
		const { pathParams } = options;

		const calculationId = pathParams?.["calculationId"];

		if (!calculationId)
			return throwError(
				() =>
					new ApplicationError(
						"No calculation id provided",
						this.constructor.name,
						this.getCalculationKpis.name
					)
			);

		return of(GET_SAVE_CALCULATION_RESPONSE);
	}

	public static deleteCalculation(options: HttpRequestOptions) {
		const { pathParams } = options;

		const calculationId = pathParams?.["calculationId"];

		if (!calculationId)
			return throwError(
				() =>
					new ApplicationError(
						"No calculation id provided",
						this.constructor.name,
						this.deleteCalculation.name
					)
			);

		return of("Calculation deleted successfully");
	}

	public static getCalculationStatus(options: HttpRequestOptions) {
		const { pathParams } = options;

		const calculationId = pathParams?.["calculationId"];

		if (!calculationId)
			return throwError(
				() =>
					new ApplicationError(
						"No calculation id provided",
						this.constructor.name,
						this.getCalculationStatus.name
					)
			);

		return of({ status: "DONE" });
	}

	public static getScenario(options: HttpRequestOptions) {
		const { body } = options;

		if (!body)
			return throwError(
				() =>
					new ApplicationError(
						"No body provided",
						this.constructor.name,
						this.getScenario.name
					)
			);

		return of({ scenarioId: "1" });
	}

	public static getScenarioStatus(options: HttpRequestOptions) {
		const { params, pathParams } = options;

		const httpParams = params as HttpParams;

		if (!httpParams)
			return throwError(
				() =>
					new ApplicationError(
						"No params provided",
						this.constructor.name,
						this.getScenarioStatus.name
					)
			);

		const tenantId = httpParams.get("tenantId");

		if (!tenantId)
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						this.constructor.name,
						this.getScenarioStatus.name
					)
			);

		const scenarioId = pathParams?.["scenarioId"];

		if (!scenarioId)
			return throwError(
				() =>
					new ApplicationError(
						"No scenario id provided",
						this.constructor.name,
						this.getScenarioStatus.name
					)
			);

		return of({ status: "DONE" });
	}

	public static deleteScenario(options: HttpRequestOptions) {
		const { params, pathParams } = options;

		const httpParams = params as HttpParams;

		if (!httpParams)
			return throwError(
				() =>
					new ApplicationError(
						"No params provided",
						this.constructor.name,
						this.deleteScenario.name
					)
			);

		const tenantId = httpParams.get("tenantId");

		if (!tenantId)
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						this.constructor.name,
						this.deleteScenario.name
					)
			);

		const scenarioId = pathParams?.["scenarioId"];

		if (!scenarioId)
			return throwError(
				() =>
					new ApplicationError(
						"No scenario id provided",
						this.constructor.name,
						this.deleteScenario.name
					)
			);

		return of("Scenario 1 deleted with success");
	}

	public static getEmissionOverviewForBarChart(options: HttpRequestOptions) {
		const { params, pathParams } = options;

		const httpParams = params as HttpParams;

		if (!httpParams)
			return throwError(
				() =>
					new ApplicationError(
						"No params provided",
						this.constructor.name,
						this.getEmissionOverviewForBarChart.name
					)
			);

		const tenantId = httpParams.get("tenantId");
		const viewMode = httpParams.get("viewMode");

		if (!tenantId)
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						this.constructor.name,
						this.getEmissionOverviewForBarChart.name
					)
			);

		if (!viewMode)
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						this.constructor.name,
						this.getEmissionOverviewForBarChart.name
					)
			);

		const scenarioId = pathParams?.["scenarioId"];

		if (!scenarioId)
			return throwError(
				() =>
					new ApplicationError(
						"No scenario id provided",
						this.constructor.name,
						this.getEmissionOverviewForBarChart.name
					)
			);

		return of(GET_EMISSION_OVERVIEW_FOR_BAR_CHART_RESPONSE);
	}

	public static getTotalEmissionByCategory(options: HttpRequestOptions) {
		const { params, pathParams } = options;

		const httpParams = params as HttpParams;

		if (!httpParams)
			return throwError(
				() =>
					new ApplicationError(
						"No params provided",
						this.constructor.name,
						this.getTotalEmissionByCategory.name
					)
			);

		const tenantId = httpParams.get("tenantId");

		if (!tenantId)
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						this.constructor.name,
						this.getTotalEmissionByCategory.name
					)
			);

		const scenarioId = pathParams?.["scenarioId"];

		if (!scenarioId)
			return throwError(
				() =>
					new ApplicationError(
						"No scenario id provided",
						this.constructor.name,
						this.getTotalEmissionByCategory.name
					)
			);

		return of(GET_TOTAL_EMISSION_BY_CATEGORY_RESPONSE);
	}

	public static getTotalEmissionByManufacturer(options: HttpRequestOptions) {
		const { params, pathParams } = options;

		const httpParams = params as HttpParams;

		if (!httpParams)
			return throwError(
				() =>
					new ApplicationError(
						"No params provided",
						this.constructor.name,
						this.getTotalEmissionByManufacturer.name
					)
			);

		const tenantId = httpParams.get("tenantId");
		const mode = httpParams.get("mode");
		const orderBy = httpParams.get("orderBy");

		if (!tenantId)
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						this.constructor.name,
						this.getTotalEmissionByManufacturer.name
					)
			);

		if (!mode || !orderBy)
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						this.constructor.name,
						this.getTotalEmissionByManufacturer.name
					)
			);

		const scenarioId = pathParams?.["scenarioId"];

		if (!scenarioId)
			return throwError(
				() =>
					new ApplicationError(
						"No scenario id provided",
						this.constructor.name,
						this.getTotalEmissionByManufacturer.name
					)
			);

		return of(
			mode === "PERCENTAGE"
				? GET_TOTAL_EMISSION_BY_MANUFACTURER_PERCENTAGE_RESPONSE
				: GET_TOTAL_EMISSION_BY_MANUFACTURER_VALUE_RESPONSE
		);
	}
}
